import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useMemo, useState } from "react";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import type { ComplianceFormQuestion } from "@/lib/compliance-api";
import { colors, fonts, gradients, radius, spacing, typography } from "@/lib/theme";

type FormAnswers = Record<number, { valueText?: string; choiceIds?: number[] }>;

interface ContractReaderProps {
    questions: ComplianceFormQuestion[];
    answers: FormAnswers;
    onRequestSignature: (questionId: number) => void;
    onSubmit: () => void;
    submitting: boolean;
    submitError: string | null;
    submitLabel: string;
}

/**
 * A "read-and-sign" document is a compliance_form whose only inputs are
 * signatures — the rest are ordered plain_text pages (e.g. the RN contract).
 * Such forms read better paged than as one long scroll, so we detect them and
 * render them with ContractReader instead of ComplianceFormSection.
 */
export function isReaderForm(questions: ComplianceFormQuestion[]): boolean {
    const pages = questions.filter((q) => q.questionType === "plain_text");
    const inputs = questions.filter((q) => q.questionType !== "plain_text");
    return (
        pages.length >= 1 &&
        inputs.length > 0 &&
        inputs.every((q) => q.questionType === "signature")
    );
}

/**
 * Multi-step reader for a read-and-sign document. Walks the ordered plain_text
 * "pages" one at a time (Back / Next + "Page X of Y"), then a final signature
 * step that captures every signature and submits. Tokens come from lib/theme.
 */
export function ContractReader({
    questions,
    answers,
    onRequestSignature,
    onSubmit,
    submitting,
    submitError,
    submitLabel
}: ContractReaderProps) {
    const { pages, signatures } = useMemo(() => {
        const sorted = [...questions].sort((a, b) => a.orderIndex - b.orderIndex);
        return {
            pages: sorted.filter((q) => q.questionType === "plain_text"),
            signatures: sorted.filter((q) => q.questionType === "signature")
        };
    }, [questions]);

    // Steps: one per page, then a final "review & sign" step.
    const totalSteps = pages.length + 1;
    const [step, setStep] = useState(0);
    const isSignStep = step >= pages.length;

    const allSigned = signatures
        .filter((q) => q.mandatory)
        .every((q) => {
            const v = answers[q.id]?.valueText;
            return !!v && v.startsWith("data:");
        });

    const progress = (step + 1) / totalSteps;

    function goNext() {
        setStep((s) => Math.min(s + 1, totalSteps - 1));
    }
    function goBack() {
        setStep((s) => Math.max(s - 1, 0));
    }

    return (
        <View style={styles.container}>
            {/* Progress header */}
            <View style={styles.progressWrap}>
                <View style={styles.progressRow}>
                    <Text style={styles.stepLabel}>
                        {isSignStep ? "Review & Sign" : `Page ${step + 1} of ${pages.length}`}
                    </Text>
                    <Text style={styles.stepCount}>
                        {step + 1}/{totalSteps}
                    </Text>
                </View>
                <View style={styles.progressTrack}>
                    <LinearGradient
                        colors={[colors.teal400, colors.pink400]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 0 }}
                        style={[styles.progressFill, { width: `${Math.round(progress * 100)}%` }]}
                    />
                </View>
            </View>

            {/* Body */}
            <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
                {!isSignStep ? (
                    <Text style={styles.prose}>{pages[step]?.label ?? ""}</Text>
                ) : (
                    <View style={styles.signStep}>
                        <Text style={styles.signIntro}>
                            You&apos;ve reached the end. Please sign below to accept this document.
                        </Text>
                        {signatures.map((q) => {
                            const signed = !!answers[q.id]?.valueText?.startsWith("data:");
                            return (
                                <View key={q.id} style={styles.sigBlock}>
                                    <Text style={styles.sigLabel}>
                                        {q.label}
                                        {q.mandatory ? " *" : ""}
                                    </Text>
                                    {signed ? (
                                        <View style={styles.signedRow}>
                                            <MaterialCommunityIcons
                                                name="check-circle"
                                                size={18}
                                                color={colors.teal600}
                                            />
                                            <Text style={styles.signedText}>Signed</Text>
                                            <Pressable
                                                onPress={() => onRequestSignature(q.id)}
                                                style={styles.resignLink}
                                            >
                                                <Text style={styles.resignText}>Re-sign</Text>
                                            </Pressable>
                                        </View>
                                    ) : (
                                        <Pressable
                                            onPress={() => onRequestSignature(q.id)}
                                            style={styles.signBtn}
                                        >
                                            <MaterialCommunityIcons
                                                name="draw"
                                                size={16}
                                                color={colors.surface}
                                            />
                                            <Text style={styles.signBtnText}>Sign</Text>
                                        </Pressable>
                                    )}
                                </View>
                            );
                        })}
                        {submitError ? <Text style={styles.error}>{submitError}</Text> : null}
                    </View>
                )}
            </ScrollView>

            {/* Footer nav */}
            <View style={styles.nav}>
                <Pressable
                    onPress={goBack}
                    disabled={step === 0}
                    style={[styles.navBtn, step === 0 && styles.navBtnDisabled]}
                >
                    <MaterialCommunityIcons
                        name="chevron-left"
                        size={20}
                        color={step === 0 ? colors.textTertiary : colors.textPrimary}
                    />
                    <Text style={[styles.navBtnText, step === 0 && styles.navBtnTextDisabled]}>
                        Back
                    </Text>
                </Pressable>

                {isSignStep ? (
                    <Pressable
                        onPress={onSubmit}
                        disabled={submitting || !allSigned}
                        style={styles.primaryWrap}
                    >
                        <LinearGradient
                            colors={[...gradients.brand]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                            style={[
                                styles.primaryBtn,
                                (submitting || !allSigned) && styles.primaryDisabled
                            ]}
                        >
                            <Text style={styles.primaryText}>
                                {submitting ? "Submitting…" : submitLabel}
                            </Text>
                        </LinearGradient>
                    </Pressable>
                ) : (
                    <Pressable onPress={goNext} style={styles.primaryWrap}>
                        <LinearGradient
                            colors={[...gradients.brand]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                            style={styles.primaryBtn}
                        >
                            <Text style={styles.primaryText}>
                                {step === pages.length - 1 ? "Continue to sign" : "Next"}
                            </Text>
                            <MaterialCommunityIcons
                                name="chevron-right"
                                size={18}
                                color={colors.surface}
                            />
                        </LinearGradient>
                    </Pressable>
                )}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.lg,
        backgroundColor: colors.surface,
        overflow: "hidden"
    },
    progressWrap: {
        padding: spacing.base,
        gap: spacing.sm,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    progressRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center"
    },
    stepLabel: {
        ...typography.subtitle,
        color: colors.textPrimary
    },
    stepCount: {
        ...typography.caption,
        color: colors.textTertiary
    },
    progressTrack: {
        height: 6,
        borderRadius: radius.full,
        backgroundColor: colors.backgroundSecondary,
        overflow: "hidden"
    },
    progressFill: {
        height: "100%",
        borderRadius: radius.full
    },
    body: {
        maxHeight: 380
    },
    bodyContent: {
        padding: spacing.base
    },
    prose: {
        ...typography.body,
        color: colors.textPrimary,
        lineHeight: 22
    },
    signStep: {
        gap: spacing.base
    },
    signIntro: {
        ...typography.body,
        color: colors.textSecondary
    },
    sigBlock: {
        gap: spacing.sm
    },
    sigLabel: {
        ...typography.subtitle,
        color: colors.textPrimary
    },
    signBtn: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm,
        backgroundColor: colors.primary,
        borderRadius: radius.sm,
        paddingVertical: 12
    },
    signBtnText: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.surface
    },
    signedRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        paddingVertical: spacing.sm
    },
    signedText: {
        ...typography.subtitle,
        color: colors.teal600
    },
    resignLink: {
        marginLeft: "auto",
        paddingHorizontal: spacing.sm,
        paddingVertical: 4,
        borderRadius: radius.xs,
        borderWidth: 1,
        borderColor: colors.primary
    },
    resignText: {
        ...typography.caption,
        color: colors.primary
    },
    error: {
        ...typography.caption,
        color: colors.error
    },
    nav: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: spacing.md,
        padding: spacing.base,
        borderTopWidth: 1,
        borderTopColor: colors.border
    },
    navBtn: {
        flexDirection: "row",
        alignItems: "center",
        gap: 2,
        paddingVertical: 10,
        paddingHorizontal: spacing.md
    },
    navBtnDisabled: {
        opacity: 0.5
    },
    navBtnText: {
        ...typography.subtitle,
        color: colors.textPrimary
    },
    navBtnTextDisabled: {
        color: colors.textTertiary
    },
    primaryWrap: {
        flex: 1,
        maxWidth: 220
    },
    primaryBtn: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm,
        borderRadius: radius.full,
        paddingVertical: 14
    },
    primaryDisabled: {
        opacity: 0.5
    },
    primaryText: {
        fontFamily: fonts.bold,
        fontSize: 15,
        color: colors.surface
    }
});
